import { lazy, Suspense, useEffect, useRef, useState } from "react";

const Turnstile = lazy(() =>
  import("@marsidev/react-turnstile").then((m) => ({ default: m.Turnstile })),
);

type Props = {
  siteKey: string;
  onSuccess: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  /** Element (usually the form) whose first interaction triggers loading Turnstile. */
  triggerRef: React.RefObject<HTMLElement | null>;
};

/**
 * Defers loading of the ~500 KiB Cloudflare Turnstile bundle until the user
 * first interacts with the form. Massively improves LCP on form pages while
 * still gating submission behind the security check.
 */
export function LazyTurnstile({ siteKey, onSuccess, onError, onExpire, triggerRef }: Props) {
  const [shouldLoad, setShouldLoad] = useState(false);
  const armedRef = useRef(false);

  useEffect(() => {
    if (shouldLoad || armedRef.current) return;
    const el = triggerRef.current;
    if (!el) return;
    armedRef.current = true;

    const trigger = () => setShouldLoad(true);
    const opts: AddEventListenerOptions = { once: true, passive: true };
    el.addEventListener("focusin", trigger, opts);
    el.addEventListener("pointerdown", trigger, opts);
    el.addEventListener("keydown", trigger, opts);

    return () => {
      el.removeEventListener("focusin", trigger);
      el.removeEventListener("pointerdown", trigger);
      el.removeEventListener("keydown", trigger);
    };
  }, [shouldLoad, triggerRef]);

  if (!shouldLoad) {
    return <div style={{ minHeight: 65 }} aria-hidden="true" />;
  }

  return (
    <Suspense fallback={<div style={{ minHeight: 65 }} aria-hidden="true" />}>
      <Turnstile
        siteKey={siteKey}
        onSuccess={onSuccess}
        onError={onError}
        onExpire={onExpire}
      />
    </Suspense>
  );
}
